import {MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import styled, {css} from 'styled-components';

import {DialogBody} from '../../../../../stateManagement/reducers/confirmDialogReducer';
import {getSelectedTaskId} from '../../../../../stateManagement/reducers/workPlanViewReducer';

import {
  BaseWorkTaskTypeEnum,
  BookingState, FutureBookingDTO,
  GetWorkPlan6BookingDTO,
  MultiDayStatusEnum,
  TaskTypeBaseDTO,
  WorkPlanItemDTO,
  WorkplanOrderDTO,
  WorkTaskStatus
} from '../../../../../api/api';
import TaskTypeService from '../../../../../services/TaskTypeService';
import {ClockIcon} from '../../../../../assets/icons/ClockIcon';
import {EyeIcon} from '../../../../../assets/icons/EyeIcon';
import InternalTimeRegForm from '../../../../../blocks/internal-timereg-form/InternalTimeRegForm';
import Button from '../../../../../components/button/Button';
import CircularProgress from '../../../../../components/circular-progress/CircularProgress';
import StatusTag from '../../../../../components/status-tag/StatusTag';
import Typography from '../../../../../components/typography/Typography';
import {useConfirmationDialog} from '../../../../../hooks/useConfirmationDialog';
import {useNavigateToTask} from '../../../../../hooks/useNavigateToTask';
import {useUpdateTaskStateDialog} from '../../../../../hooks/useUpdateTaskStateDialog';
import {WorkPlanItemDTOExtended} from '../../../../../models/WorkPlanItem';
import NotificationService from '../../../../../services/NotificationService';
import TaskService from '../../../../../services/TaskService';
import {formatDateHourString, getDistanceInDaysFromToday, getFullDateString} from '../../../../../utils/dateHandling';
import {getEnumDisplayValue} from '../../../../../utils/enumUtils';
import {log} from '../../../../../utils/logging/log';
import {getAddress} from '../../../../../utils/stringHandling';
import BasicPopover from '../../../../../components/popover/Popover';
import Person from '../../../../../components/person/Person';
import ContactCard from '../../../../../components/contact-card/ContactCard';
import {RowValue} from '../../../task-details-view2/components/TextRow';
import {BookingCount} from "./BookingCount";
import {WorkTaskActionButton} from "./WorkTaskActionButton";
import TransferResponsibilityDialog from '../../../../../components/transfer-responsibility-dialog/TransferResponsibilityDialog';
import {userAllowedThroughFeatureFlag} from '../../../../../utils/validation/userAllowedFeatureFlag';

export interface BookingTaskProps {
  task: WorkPlanItemDTOExtended;
  handleSelect: (taskId: number | undefined) => void;
  fetchTasks: () => void;
  isCalendar?: boolean;
  workPlanOrders?: WorkplanOrderDTO[];
  loadingWorkPlanOrders?: boolean;
  booking: GetWorkPlan6BookingDTO;
}

// The task is only worth handing on when more than one booking is still to be worked on it - a task
// with a single booking has nobody to hand it to.
const isSharedWithOtherBookings = (task: WorkPlanItemDTOExtended) =>
  (task.bookingInfo?.numberOfBookingsOnTask ?? 0) > 1;

// A settled task has no responsibility left to hand on, for good (afsluttet/udført/annulleret) or for
// today (pause).
const isTaskStillOpen = (status?: WorkTaskStatus) =>
  status !== WorkTaskStatus.Completed &&
  status !== WorkTaskStatus.Processed &&
  status !== WorkTaskStatus.Canceled &&
  status !== WorkTaskStatus.Pause;

const showDetailsCalculator = (props: BookingTaskProps, selected: boolean) => {
  if (selected) return true; // Always show details if selected
  if (props.isCalendar) return false; // Don't show details in calendar view unless selected
  if (props.task.status === WorkTaskStatus.Processed) return false; // Don't show details for processed/afsluttet tasks unless selected
  if (props.task.status === WorkTaskStatus.Pause) return false; // Don't show details for paused tasks unless selected
  if (props.task.status === WorkTaskStatus.Completed) return false; // Don't show details for completed/udført tasks unless selected
  if (props.booking.state == BookingState.OnRoute || props.booking.state == BookingState.OnGoing) return true; // Show details if booking is active
  return false;
}

const DayOverviewTaskInBooking = (props: BookingTaskProps) => {
  const {
    task,
    handleSelect,
    fetchTasks,
    isCalendar = false,
    loadingWorkPlanOrders,
    workPlanOrders,
    booking
  } = props;
  const [taskStatus, setTaskStatus] = useState<WorkTaskStatus>(task.status as WorkTaskStatus);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [openInternalTimeRegistrationForm, setOpenInternalTimeRegistrationForm] = useState(false);
  const [resolvedTaskType, setResolvedTaskType] = useState<TaskTypeBaseDTO | undefined>(task.taskType);
  const [openTransferResponsibility, setOpenTransferResponsibility] = useState(false);

  useEffect(() => {
    if (task.baseWorkTaskType !== BaseWorkTaskTypeEnum.InternalWorkTask) return;
    if (task.taskType?.projectNumber_OneERP) return;
    TaskTypeService.getInternalTaskTypes().then((types) => {
      const match = types.find((t) => t.id === task.taskType?.id);
      if (match) setResolvedTaskType(match);
    });
  }, [task.taskType?.id, task.baseWorkTaskType]);

  const expandedTaskId = useSelector(getSelectedTaskId);

  const selected = useMemo(() => {
    return expandedTaskId === task.workTaskId;
  }, [expandedTaskId, task.workTaskId]);
  
  const showTaskDetails = showDetailsCalculator(props, selected);

  const navigateToTask = useNavigateToTask();
  const { getTaskStatusUpdateDialog } = useUpdateTaskStateDialog();
  const { getConfirmation } = useConfirmationDialog();
  const ref = useRef<HTMLInputElement>(null);

  const handleOnClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (task.baseWorkTaskType === BaseWorkTaskTypeEnum.Absence) return;
    handleSelect(task.workTaskId);
  };

  const handleOpenTask = useCallback(
    (e?: any) => {
      e && e.stopPropagation();
      if (!task.workTaskId || !task.baseWorkTaskType) return;
      navigateToTask(task.workTaskId, task.baseWorkTaskType);
    },
    [task.workTaskId, task.baseWorkTaskType, navigateToTask]
  );

  const statusUpdatedSuccessCallback = useCallback(
    (newStatus: WorkTaskStatus) => {
      NotificationService.success(`"${getEnumDisplayValue(newStatus)}"`);
      setTaskStatus(newStatus);
      setUpdatingStatus(false);

      if (newStatus === WorkTaskStatus.Ongoing) {
        if (
          task.baseWorkTaskType === BaseWorkTaskTypeEnum.WorkTask ||
          task.baseWorkTaskType === BaseWorkTaskTypeEnum.SubWorkTask
        ) {
          fetchTasks();
          handleOpenTask();
        } else if (task.baseWorkTaskType === BaseWorkTaskTypeEnum.InternalWorkTask) {
          // Refresh the workplan so the booking state (OnGoing) is reflected in the UI.
          // Internal tasks do not navigate on start — the user stays in the booking view.
          fetchTasks();
        }
      }
    },
    [handleOpenTask, task.baseWorkTaskType, fetchTasks]
  );

  const updateStatus = useCallback(
    (id: number, workTaskStatus: WorkTaskStatus, flsId?: number, successCallback?: (newStatus: WorkTaskStatus) => void) => {
      setUpdatingStatus(true);
      TaskService.setWorkPlanTaskStatus({
        workTaskId: id,
        status: workTaskStatus,
        flsId: flsId ?? -1
      })
        .then(async (res) => {
          if (workTaskStatus === WorkTaskStatus.Ongoing) {
            await TaskService.startBooking(booking.id as number);
          }
          successCallback && successCallback(res.status);
        })
        .catch((error) => {
          log(error);
          NotificationService.error('Der skete en fejl. Kunne ikke ændre opgavestatus.');
        })
        .finally(() => {
          setUpdatingStatus(false);
        });
    },
    []
  );

  const handleUpdateStatus = useCallback(
    async (e?: any) => {
      e && e.stopPropagation();
      if (!task.workTaskId) {
        return;
      }

      if (taskStatus === WorkTaskStatus.Completed) {
        NotificationService.error(
          `Kan ikke starte opgaven, da den er "${getEnumDisplayValue(WorkTaskStatus['Completed'])}"`
        );
        return;
      }

      if (taskStatus === WorkTaskStatus.Ongoing) {
        if (booking.state === BookingState.Planned || booking.state === BookingState.OnRoute) {
          TaskService.startBooking(booking.id as number).then(() => {
            fetchTasks();
            if (task.baseWorkTaskType !== BaseWorkTaskTypeEnum.InternalWorkTask) {
              handleOpenTask();
            }
          });
        } else if (task.workTaskId && task.baseWorkTaskType && task.taskType?.id) {

          const hasFutureBookings = task.futureBookings !== undefined && task.futureBookings?.length > 0

          await getTaskStatusUpdateDialog(
            task.workTaskId,
            task.baseWorkTaskType,
            task.taskType?.id,
            task.flsId ?? -1,
             hasFutureBookings,
            taskStatus,
            booking.id
          );
          fetchTasks();
        }
      } else if (taskStatus === WorkTaskStatus.Planned || taskStatus === WorkTaskStatus.PlannedAppointment) {
        updateStatus(task.workTaskId, WorkTaskStatus.Ongoing, task.flsId, (newStatus) =>
          statusUpdatedSuccessCallback(newStatus)
        );
      }
    },
    [
      task.workTaskId,
      task.baseWorkTaskType,
      task.taskType?.id,
      task.flsId,
      task.multiDayStatus,
      taskStatus,
      workPlanOrders,
      getTaskStatusUpdateDialog,
      fetchTasks,
      updateStatus,
      statusUpdatedSuccessCallback
    ]
  );

  const handleNavigate = async (e?: any) => {
    e && e.stopPropagation();
    const latLngUrl =
      'https://www.google.com/maps/dir/?api=1&destination=' +
      task.tasklocation?.latitude +
      ',' +
      task.tasklocation?.longitude;
    window.open(latLngUrl, '_top');
    let statusUpdated = false;

    if (task.workTaskId && (taskStatus === WorkTaskStatus.Planned || taskStatus === WorkTaskStatus.PlannedAppointment)) {
      statusUpdated = true;
      updateStatus(task.workTaskId, WorkTaskStatus.OnRoute, task.flsId, () =>
        statusUpdatedSuccessCallback(WorkTaskStatus.OnRoute)
      );
    }

    const confirmation = await getConfirmation(navigateDialogBody);
    if (confirmation === 'confirm') {
      if (task.workTaskId && (statusUpdated || taskStatus === WorkTaskStatus.OnRoute)) {
        updateStatus(task.workTaskId, WorkTaskStatus.Ongoing, task.flsId, () =>
          statusUpdatedSuccessCallback(WorkTaskStatus.Ongoing)
        );
      } else if (task.baseWorkTaskType !== BaseWorkTaskTypeEnum.InternalWorkTask) {
        handleOpenTask();
      }
    }
  };

  const renderTextContent = (task: WorkPlanItemDTO) => (
    <TextContent>
      {
        showTaskDetails && task.baseWorkTaskType !== BaseWorkTaskTypeEnum.InternalWorkTask &&
          <RowCentered>
            <TextItem>
              <b>Første mand</b>
            </TextItem>
            <StyledRowValue>
              {task.bookingInfo?.technicalResponsible ? (
                  <BasicPopover
                      buttonElement={<Person name={task.bookingInfo?.technicalResponsible?.name} />}
                      popoverElement={<ContactCard email={task.bookingInfo?.technicalResponsible?.email} />}
                  />
              ) : (
                  <Typography fontStyle="italic">Ingen tekniker valg</Typography>
              )}

            </StyledRowValue>
          </RowCentered>
      }


      {showTaskDetails && (
        <>
          {task.concurrentTasks?.map(
            (c, index) =>
              c.taskType && (
                <Row key={index}>
                  <TextItem flex="1">
                    <b>{getEnumDisplayValue(c.taskType)}:</b>
                  </TextItem>
                  <TextItem flex="3">{c.assignedTo}</TextItem>
                </Row>
              )
          )}
        </>
      )}
      {task.notesForTechnician && (
        <Row>
          <TextItem>
            <b>Note til tekniker</b>
          </TextItem>
          <TextItem flex={showTaskDetails ? '3' : '1'}>{task.notesForTechnician}</TextItem>
        </Row>
      )}
      {showTaskDetails && task.baseWorkTaskType !== BaseWorkTaskTypeEnum.InternalWorkTask && (
        <Row>
          <TextItem>
            <b>Bestillinger</b>
          </TextItem>
          <DropPointsContainer>
            <TextItem flex={'3'}>
              {loadingWorkPlanOrders ? (
                <StyledSpinner size="12px" />
              ) : (
                <>
                  {workPlanOrders ? (
                    <>
                      {workPlanOrders?.length > 0 ? (
                        <>
                          {workPlanOrders?.map((order) => {
                            return (
                              <DropPoint>
                                {`Dropsted ${order.dropPoint?.city} - ${getAddress(order.dropPoint)}`}
                                <StatusTag margin="0 0 0 20px" orderStatus={order.order?.orderStatus}>
                                  {order.order?.orderStatus ? getEnumDisplayValue(order.order?.orderStatus) : '-'}
                                </StatusTag>
                              </DropPoint>
                            );
                          })}
                        </>
                      ) : (
                        <TextItem flex={showTaskDetails ? '3' : '1'}>Ingen bestillinger til opgaven</TextItem>
                      )}
                    </>
                  ) : (
                    <TextItem flex={showTaskDetails ? '3' : '1'}>
                      Bestillinger kunne ikke hentes. Prøv igen senere
                    </TextItem>
                  )}
                </>
              )}
            </TextItem>
          </DropPointsContainer>
        </Row>
      )}
    </TextContent>
  );

  if (task.plannedArrival === undefined) {
    return <></>;
  }

  return (
    <>
      <InternalTimeRegForm
        taskId={task.workTaskId}
        open={openInternalTimeRegistrationForm}
        handleReturn={() => setOpenInternalTimeRegistrationForm(false)}
        taskType={resolvedTaskType}
      />

      <TransferResponsibilityDialog
        open={openTransferResponsibility}
        workTaskId={task.workTaskId}
        handleClose={() => setOpenTransferResponsibility(false)}
        onTransferred={fetchTasks}
      />

      <Container
        workTaskType={task.baseWorkTaskType}
        showDetails={showTaskDetails}
        isCompleeted={
          task.status === WorkTaskStatus.Completed ||
          task.status === WorkTaskStatus.Pause ||
          task.status === WorkTaskStatus.Processed
        }
        onClick={handleOnClick}
      >
        <StyledHeader>
          <TextContainer>
            {task.plannedArrival && (
              <Row>
                <Typography variant="p" color="textMenu">
                  {getFullDateString(new Date(task.plannedArrival))}
                </Typography>
              </Row>
            )}
            <Typography variant={showTaskDetails ? 'h4' : 'b'}>
              {task.baseWorkTaskType === BaseWorkTaskTypeEnum.Absence ? (
                task.notes
              ) : (
                <>
                  {task.taskType?.name} - {task?.workTaskId}
                </>
              )}
            </Typography>
          </TextContainer>
            <RowCenteredGap>
              <BookingCount key={task.workTaskId} bookingInfo={task.bookingInfo} />
              <StatusContainer>
                {task.baseWorkTaskType !== BaseWorkTaskTypeEnum.Absence && (
                    <StyledStatusTag workTaskStatus={taskStatus ? taskStatus : undefined}>
                      {taskStatus && getEnumDisplayValue(taskStatus)}
                    </StyledStatusTag>
                )}
                {<SelectedTime>
                  <Typography variant="h4">
                    {task.appointmentTimeStart && task.appointmentTimeEnd
                        ? `${formatDateHourString(task.appointmentTimeStart)}\xa0- ${formatDateHourString(
                            task.appointmentTimeEnd
                        )}`
                        : ''}
                  </Typography>
                </SelectedTime>}
              </StatusContainer>
            </RowCenteredGap>
        </StyledHeader>


        <Row>{renderTextContent(task)}</Row>

        {(showTaskDetails) && task.baseWorkTaskType !== BaseWorkTaskTypeEnum.Absence && (
          <Column moveRight={isCalendar}>
            <StyledbuttonContainer ref={ref}>
              {
                !isCalendar
                  && <WorkTaskActionButton task={task} booking={ booking} taskStatus={taskStatus} handleUpdateStatus={handleUpdateStatus} updatingStatus={updatingStatus} isCalendar={isCalendar} />
              }
              {task.baseWorkTaskType === BaseWorkTaskTypeEnum.InternalWorkTask ? (
                <Button variant="secondary" onClick={() => setOpenInternalTimeRegistrationForm(true)}>
                  <ClockIcon size="18px" />
                  Registrer intern tid
                </Button>
              ) : (
                <>
                  <Button data-testid="open-task-button" variant="secondary" onClick={(e) => handleOpenTask(e)}>
                    {ref?.current?.offsetWidth && ref.current.offsetWidth > 525 && <EyeIcon size="18px" />}
                    Åbn opgave
                  </Button>
                  {/* Only "første mand" can close the task, so only they have a responsibility to hand
                      on - and only when someone else is booked on it to hand it to. */}
                  {userAllowedThroughFeatureFlag(window._env_.FeatureFlags_CloseTaskBookingsInfo) &&
                    task.isCloseAllowed &&
                    isTaskStillOpen(taskStatus) &&
                    isSharedWithOtherBookings(task) && (
                    <Button
                      data-testid="transfer-responsibility-button"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTransferResponsibility(true);
                      }}
                    >
                      Overdrag ansvar
                    </Button>
                  )}
                </>
              )}
            </StyledbuttonContainer>
          </Column>
        )}
      </Container>
    </>
  );
};

const navigateDialogBody: DialogBody = {
  headerText: 'Ankommet?',
  bodyText: 'Er du ankommet til destinationen?',
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Bekræft'
};

const futureBookingsWarning: DialogBody = {
  headerText: 'Fremtidig bookinger',
  bodyText: 'Du er ved at lukke en opgave som har fremtidige bookinger. Er du sikker?',
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Ja, luk opgave alligevel'
};

const createFutureBookingsWarning = (futureBookings: FutureBookingDTO[]): DialogBody => {
  const bookingLines = (futureBookings ?? [])
    .map((b) => {
      
      return ` • ${b.person?.name} (${b.person?.email}) - ${getFullDateString(new Date(b.appointmentStart!))}`; 
    })
    .join('\n');

  const bodyText = [
    'Du er ved at lukke en opgave som har følgende bookinger planlagt i fremtiden:',
    '',
    bookingLines || '• (ingen bookinger fundet)',
    '',
    'Er du sikker?'
  ].join('\n');

  return {
    headerText: 'Fremtidig bookinger',
    bodyText,
    declineButtonText: 'Fortryd',
    confirmButtonText: 'Ja, luk opgave alligevel'
  };
};

const Container = styled.div<{
  workTaskType: BaseWorkTaskTypeEnum | undefined;
  showDetails?: boolean;
  isCompleeted?: boolean;
}>`
  background: ${(props) =>
    props.workTaskType === BaseWorkTaskTypeEnum.InternalWorkTask
      ? props.theme.palette.functions.warning.tertiary
      : props.isCompleeted && !props.showDetails
      ? props.theme.palette.grey.black5
      : props.theme.palette.background.primary};

  background: ${(props) =>
    props.workTaskType === BaseWorkTaskTypeEnum.Absence && props.theme.palette.functions.action.tertiary};

  margin: 12px;
  padding: 16px 24px 16px 32px;

  box-shadow: 0px 6px 14px rgba(0, 0, 0, 0.15);
  border: 1px solid #e3e3e3;
  border-radius: 12px;
  transition: margin 250ms;

  ${({ showDetails }) =>
    showDetails &&
    css`
      // padding: 32px 24px 32px 32px;
      //margin: 24px 12px !important;
    `};
`;

const StyledHeader = styled.div`
  text-align: start;
  display: flex;
  justify-content: space-between;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  column-gap: 12px;
  text-align: start;
`;

const RowCentered = styled.div`
  display: flex;
  column-gap: 4px;
  align-items: center;
  text-align: start;
`;

const RowCenteredGap = styled.div`
  display: flex;
  column-gap: 12px;
  align-items: center;
  text-align: start;
`;

const TextItem = styled.div<{ flex?: string }>`
  ${(props) => ({ ...props.theme.typography.p })};
  flex: ${(props) => props.flex};
  text-align: start;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const Column = styled.div<{ moveRight?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  margin-top: 16px;

  column-gap: 8px;
  row-gap: 8px;

  ${({ moveRight }) =>
    moveRight &&
    css`
      margin-top: 0px;
      flex-direction: column;
      align-items: end;
    `};
`;

const StyledbuttonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 8px;
  row-gap: 8px;
`;
const FlexGrow = styled.div`
  flex-grow: 1;
  display: flex;
`;

const SelectedTime = styled.span`
  display: inline-flex;
  margin-left: 12px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatusContainer = styled.div<{ selected?: boolean }>`
  text-align: end;

  ${({ selected }) =>
    selected &&
    css`
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      align-items: center;
    `};
`;

const StyledStatusTag = styled(StatusTag)`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
`;

const DropPointsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropPoint = styled.div`
  display: flex;
  alignitems: center;
  margin-bottom: 10px;
`;

const StyledSpinner = styled(CircularProgress)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  height: 20px;
`;

const StyledRowValue = styled(RowValue)`
  display: flex;
  align-items: center;
`;

export default DayOverviewTaskInBooking;
